"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { addToCart, readCart } from "@/lib/cart-client";
import { track, type TrackedItem } from "@/lib/track";

type Props = {
  productId: string;
  locale: string;
  label: string;
  addedLabel: string;
  inCartLabel: string;
  variant?: "primary" | "gold" | "outline";
  buyNow?: { label: string } | null;
  className?: string;
  /** Sert uniquement a la mesure d'audience ; absent, l'evenement part sans detail. */
  item?: TrackedItem | null;
  currency?: string;
};

export default function AddToCartButton({
  productId,
  locale,
  label,
  addedLabel,
  inCartLabel,
  variant = "primary",
  buyNow = null,
  className = "",
  item = null,
  currency = "CHF",
}: Props) {
  const router = useRouter();
  const [inCart, setInCart] = useState(false);
  const [justAdded, setJustAdded] = useState(false);

  useEffect(() => {
    const sync = () => setInCart(readCart().some((l) => l.id === productId));
    sync();
    window.addEventListener("cart:changed", sync);
    return () => window.removeEventListener("cart:changed", sync);
  }, [productId]);

  useEffect(() => {
    if (!justAdded) return;
    const timer = setTimeout(() => setJustAdded(false), 2200);
    return () => clearTimeout(timer);
  }, [justAdded]);

  const measure = (event: string) => {
    track(event, {
      currency,
      valueCents: item?.priceCents,
      items: item ? [{ ...item, quantity: 1 }] : undefined,
    });
  };

  const handleAdd = () => {
    addToCart(productId);
    measure("add_to_cart");
    setJustAdded(true);
  };

  const handleBuyNow = () => {
    addToCart(productId);
    measure("add_to_cart");
    measure("begin_checkout");
    router.push(`/${locale}/panier`);
  };

  const variantClass =
    variant === "gold"
      ? "btn-gold"
      : variant === "outline"
        ? "btn-outline"
        : "btn-primary";

  return (
    <div className={`flex flex-wrap gap-2.5 ${className}`}>
      <button
        type="button"
        onClick={handleAdd}
        className={`btn ${variantClass}`}
        aria-live="polite"
      >
        {justAdded ? (
          <>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="m5 12 5 5L20 7" />
            </svg>
            {addedLabel}
          </>
        ) : inCart ? (
          inCartLabel
        ) : (
          label
        )}
      </button>

      {buyNow ? (
        <button type="button" onClick={handleBuyNow} className="btn btn-outline">
          {buyNow.label}
        </button>
      ) : null}
    </div>
  );
}
